<template>
    <div class="is-flex is-align-items-center single-competitor" :class="competitorClasses">
        <CompetitorAvatar
            v-if="showAvatar"
            :size="28"
            :first-name="competitor.firstName"
            :last-name="competitor.lastName"
            :photo-url="competitor.photoUrl ?? null"
            class="mr-2 is-flex-shrink-0"
        />
        <div class="is-flex-grow-1">
            <p>
                <Country :country-code="competitor.nationality" :only-flag="true" />

                <a v-if="competitor.id > 0 && selectable" class="clarity-mask" @click="emit('selected', competitor.id)">
                    {{ competitor.firstName }}
                    {{ competitor.lastName }}
                </a>
                <span v-else class="clarity-mask">
                    {{ competitor.firstName }}
                    {{ competitor.lastName }}
                </span>

                <span v-if="!competitor.status.verified && !competitor.status.disqualified" class="ml-1 tag is-danger is-small">
                    {{ translate("common.unverified.abbrv") }}
                </span>
                <span v-else-if="competitor.status.disqualified" class="ml-1 tag is-warning is-small">
                    {{ translate("common.disqualified.abbrv") }}
                </span>
            </p>
            <p class="academy-name" v-if="competitor.academyId > 0">
                {{ competitor.academy }}
                <template v-if="showBranch && competitor.branch">
                    <span class="branch-separator">/</span>
                    <span class="branch-name">{{ competitor.branch }}</span>
                </template>
            </p>
        </div>

        <slot name="after-name" :competitor="competitor" />
    </div>
</template>

<script setup lang="ts">
    import Country from "../Country.vue";
    import CompetitorAvatar from "../Competitors/CompetitorAvatar.vue";
    import { computed } from "vue";
    import translatorMixin from "../translatorMixin";
    import { RegisteredCompetitorWithoutParameterId } from "../Competitors/types";
    import type { ColorSchema } from "../types";

    const { translate } = translatorMixin.methods;
    type CompetitorSide = "first" | "second";

    const props = withDefaults(
        defineProps<{
            competitor: RegisteredCompetitorWithoutParameterId;
            selectable?: boolean;
            isWinner?: boolean;
            showBranch?: boolean;
            showAvatar?: boolean;
            competitorSide?: CompetitorSide;
            colorSchema?: ColorSchema;
        }>(),
        {
            colorSchema: "default",
            competitorSide: undefined,
            showAvatar: false,
        },
    );

    const competitor = computed(() => props.competitor);
    const competitorClasses = computed(() => {
        if (!props.competitorSide) {
            return {};
        }

        return {
            [`${props.competitorSide}-competitor`]: true,
            [props.colorSchema]: true,
        };
    });

    const emit = defineEmits(["selected"]);
</script>

<style scoped>
    .first-competitor {
        background-color: var(--competitor-first-default-bg);
        color: var(--competitor-first-default-color);
    }

    .second-competitor {
        background-color: var(--competitor-second-default-bg);
        color: var(--competitor-second-default-color);
    }

    .first-competitor.redblue {
        background-color: var(--competitor-first-redblue-bg);
        color: var(--competitor-first-redblue-color);
    }

    .second-competitor.redblue {
        background-color: var(--competitor-second-redblue-bg);
        color: var(--competitor-second-redblue-color);
    }

    .second-competitor.bluered {
        background-color: var(--competitor-second-bluered-bg);
        color: var(--competitor-second-bluered-color);
    }

    .first-competitor.whiteblue {
        background-color: var(--competitor-first-whiteblue-bg);
        color: var(--competitor-first-whiteblue-color);
    }

    .second-competitor.whiteblue {
        background-color: var(--competitor-second-whiteblue-bg);
        color: var(--competitor-second-whiteblue-color);
    }

    .first-competitor.redwhite {
        background-color: var(--competitor-first-redwhite-bg);
        color: var(--competitor-first-redwhite-color);
    }

    .second-competitor.redwhite {
        background-color: var(--competitor-second-redwhite-bg);
        color: var(--competitor-second-redwhite-color);
    }

    .first-competitor a,
    .second-competitor.redblue a,
    .second-competitor.bluered a,
    .second-competitor.whiteblue a,
    .first-competitor.redwhite a {
        color: white;
        text-decoration: underline;
    }

    .first-competitor a:hover,
    .second-competitor.redblue a:hover,
    .second-competitor.bluered a:hover,
    .second-competitor.whiteblue a:hover,
    .first-competitor.redwhite a:hover {
        color: #f0f0f0;
    }

    .second-competitor a,
    .first-competitor.whiteblue a,
    .second-competitor.redwhite a {
        color: #000;
        text-decoration: underline;
    }

    .second-competitor a:hover,
    .first-competitor.whiteblue a:hover,
    .second-competitor.redwhite a:hover {
        color: #333;
    }

    .second-competitor.default :deep(.metric-value),
    .first-competitor.whiteblue :deep(.metric-value),
    .second-competitor.redwhite :deep(.metric-value) {
        color: rgba(0, 0, 0, 0.9);
    }

    .second-competitor.default :deep(.metric-label),
    .first-competitor.whiteblue :deep(.metric-label),
    .second-competitor.redwhite :deep(.metric-label) {
        color: rgba(0, 0, 0, 0.5);
    }

    .second-competitor.default :deep(.call-metrics),
    .first-competitor.whiteblue :deep(.call-metrics),
    .second-competitor.redwhite :deep(.call-metrics) {
        background: rgba(0, 0, 0, 0.06);
    }

    .second-competitor.default :deep(.metric-divider),
    .first-competitor.whiteblue :deep(.metric-divider),
    .second-competitor.redwhite :deep(.metric-divider) {
        background: rgba(0, 0, 0, 0.15);
    }

    .second-competitor.default :deep(.call-action-button),
    .first-competitor.whiteblue :deep(.call-action-button),
    .second-competitor.redwhite :deep(.call-action-button) {
        border-color: rgba(0, 0, 0, 0.15);
        background: rgba(0, 0, 0, 0.06);
    }

    .second-competitor.default :deep(.call-action-button:hover),
    .first-competitor.whiteblue :deep(.call-action-button:hover),
    .second-competitor.redwhite :deep(.call-action-button:hover) {
        background: rgba(0, 0, 0, 0.1);
        border-color: rgba(0, 0, 0, 0.25);
    }

    .second-competitor.default :deep(.button-content),
    .first-competitor.whiteblue :deep(.button-content),
    .second-competitor.redwhite :deep(.button-content) {
        color: rgba(0, 0, 0, 0.8);
    }
</style>
